import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { DaylightCardLink } from "../../../../modules/content/components/DaylightCardLink/DaylightCardLink";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { ITextContentGridBlockProps } from "./TextContentGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

export const TextContentGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITextContentGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-12 md:gap-16">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          layout="rail"
        />
        <div
          className={cn(
            "grid grid-cols-1 border-l border-t border-border sm:grid-cols-2",
            columns[columnCount || 3],
          )}
        >
          {items.map((item, index) => (
            <DaylightCardLink
              className={cn(
                "relative border-b border-r border-border p-8 transition-colors duration-300 hover:bg-panel",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <span className="daylight-label mb-8 block text-northsea">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="mb-4 font-daylight-display text-2xl font-semibold tracking-[-0.04em] text-graphite">
                {item.heading}
              </h3>
              {item.description ? (
                <DaylightRichText
                  className="mb-8 min-h-20 max-w-[38ch] text-sm leading-relaxed text-northsea"
                  content={item.description}
                />
              ) : null}
              {item.cardLink ? (
                <Icon
                  className="absolute bottom-6 right-6 size-5 translate-x-2 translate-y-2 text-primary opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:translate-y-0 group-focus-visible:opacity-100"
                  name="arrow-up-right"
                />
              ) : null}
            </DaylightCardLink>
          ))}
        </div>
      </div>
    </DaylightSection>
  );
};

TextContentGridBlock.displayName = "Block::TextContentGridBlock";
